import { useRecoilValue } from 'recoil'
import { useRouter } from 'next/router'
import { loadStripe } from '@stripe/stripe-js'
import { userInfoState } from '@/util/states'

const stripePromise = loadStripe(process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY || '')

interface TipCreatorParams {
  creatorId: string
  creatorUsername?: string
  amount: number // Amount in cents (e.g., 500 = $5.00)
  context?: 'live'
}

export const useTipCreator = () => {
  const userInfo = useRecoilValue(userInfoState)
  const router = useRouter()

  const tipCreator = async ({ creatorId, creatorUsername, amount, context }: TipCreatorParams) => {
    if (userInfo === 'pending') {
      console.log('User info is still loading')
      return
    }

    if (!userInfo) {
      // Redirect to signup if not logged in
      router.push({
        pathname: '/signup',
        query: { returnUrl: router.asPath }
      })
      return
    }

    // Prevent users from tipping themselves
    if (userInfo.uid === creatorId) {
      alert('You cannot tip yourself!')
      return
    }

    // Validate amount
    if (amount < 500) {
      console.error('Tip amount must be at least $5.00')
      alert('Tip amount must be at least $5.00')
      return
    }

    if (amount > 10000000) {
      console.error('Tip amount cannot exceed $100,000')
      alert('Tip amount cannot exceed $100,000')
      return
    }

    try {
      const res = await fetch('/api/tip/checkout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          amount,
          creatorId,
          creatorUsername,
          fromUserId: userInfo.uid,
          returnUrl: router.asPath,
          ...(context ? { context } : {})
        })
      })

      if (!res.ok) {
        const error = await res.json()
        console.error('Failed to create tip checkout:', error)
        alert('Failed to create tip checkout. Please try again.')
        return
      }

      const { sessionId } = await res.json()

      const stripe = await stripePromise
      if (stripe) {
        const { error } = await stripe.redirectToCheckout({ sessionId })
        if (error) {
          console.error('Stripe redirect error:', error)
          alert('Failed to redirect to checkout. Please try again.')
        }
      } else {
        console.error('Stripe object was null')
        alert('Failed to load payment system. Please try again.')
      }
    } catch (err) {
      console.error('Tip checkout failed:', err)
      alert('Something went wrong. Please try again.')
    }
  }

  return { tipCreator }
}

